(ns frontend.view.modals.curtains.pipeline-settings-curtain.view
  (:require
   [common.localization :as loc]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.entity :as f-entity]
   [frontend.view.modals.curtains.pipeline-settings-curtain.events :as events]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.exit-opportunity-stage-delete.view :as exit-opportunity-stage-delete-modal]
   [frontend.view.modals.opportunity-stage-delete.view :as opportunity-stage-delete-modal]
   [frontend.view.modals.sales-opportunity-stage-delete.view :as sales-opportunity-stage-delete-modal]
   [frontend.view.modals.stage-delete.view :as stage-delete-modal]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.components.sortable-list.view :as sortable-list]
   [ui.editors.data-input :as data-input]
   [ui.util :as uu]))

(defonce modal-key :curtain/pipeline-settings)

(def stage-name-attribute->creation-flow?
  "Per pipeline-entity-type gate for the stage lock/unlock (prevent-create?)
   toggle - mirrors frontend.react.virtualized.scrollable-grid.kanban's
   stage-attribute->access-fn, keyed by stage-name-attribute instead since
   that's what stage-view already receives."
  {:opportunity-stage/name       session/froppo-creation-flow?
   :sales-opportunity-stage/name session/sales-oppo-creation-flow?
   :stage/name                   session/deal-creation-flow?
   :exit-opportunity-stage/name  session/exit-oppo-creation-flow?})

(defn edit-view
  [edit-state {:keys [stage field-schema stage-name-attribute pipeline-id]}]
  [:div
   {:class "w-full flex justify-between items-center gap-3"}
   [data-input/view
    {:data-type :data.type/text-simple
     :class     "flex-1"
     :value     (get @edit-state stage-name-attribute)
     :on-change (fn [new-value]
                  (swap! edit-state assoc stage-name-attribute new-value))}]
   [:div
    {:class "flex gap-1"}
    (let [tooltip-text (events/tooltip (get @session/entities pipeline-id)
                                       edit-state
                                       field-schema
                                       stage-name-attribute)
          disabled? (boolean tooltip-text)]
      [button/square-secondary-open
       ;; `:disabled?` is the prop the button reads; `:disabled` was discarded,
       ;; so this rendered enabled even when the name failed validation. The
       ;; on-click guarded itself, so it did nothing when pressed - the button
       ;; just did not look disabled.
       {:disabled?  disabled?
        ;; tooltip-text is the validation message and is nil when the name is
        ;; valid, which left the button unnamed in the ordinary case.
        :tooltip    (or tooltip-text (loc/tr :save))
        :aria-label (loc/tr :save)
        :on-click   (fn [_e]
                      (when-not disabled?
                        (events/save! edit-state
                                      stage-name-attribute
                                      (get stage stage-name-attribute)
                                      (:db/id stage))
                        (deref edit-state)))}
       [:i.fal.fa-save]])
    [button/square-secondary-open
     {:tooltip    (loc/tr :cancel)
      :aria-label (loc/tr :cancel)
      :on-click   #(events/end-edit! edit-state)}
     [:i.fal.fa-times]]]])

(defn stage-view
  [_]
  (r/create-class
   {:reagent-render
    (fn [prevent-delete? edit-state _drag-state
         {:keys [stage-name-attribute pipeline-id stage field-schema
                 prevent-create-config first-stage? last-stage?]}]
      (let [stage-id (:db/id stage)
            stage-name (get stage stage-name-attribute)
            creation-flow-fn (get stage-name-attribute->creation-flow? stage-name-attribute)

            delete-tooltip (if prevent-delete?
                             (loc/tr :stage-delete-last)
                             (loc/tr :stage-delete [stage-name]))
            prevent-create? (= (:setting prevent-create-config)
                               :prevent)
            inherited? (:inherited? prevent-create-config)
            prevent-create-disabled? (or first-stage?
                                         inherited?)
            edit @edit-state
            edit-id (:db/id edit)]
        [:div
         {:class "py-1 px-2 flex items-center h-12 gap-2 relative group border overflow-hidden
                  bg-base-light hover:bg-base-regular active:bg-base-regular rounded cursor-grab"}
         [:div
          [sortable-list/drag-handle-icon]]

         (if (= stage-id edit-id)
           [edit-view edit-state
            {:stage                stage
             :field-schema         field-schema
             :stage-name-attribute stage-name-attribute
             :pipeline-id          pipeline-id}]
           [:div
            {:class "flex items-center justify-between grow-[2]"
             :id    (str "edit-stage-" pipeline-id "-" stage-id)}
            [:span
             {:class "pl-2"}
             stage-name]
            [:span
             {:class "flex gap-1"}
             [button/square-secondary-open
              {:dom-id     (str "edit-stage-" pipeline-id "-" stage-id "-edit-button")
               :tooltip    (loc/tr :pipeline-settings/rename-stage)
               :aria-label (loc/tr :pipeline-settings/rename-stage)
               :on-click   #(events/start-edit! edit-state stage)}
              [:i.fal.fa-edit]]
             (when (and (fn? creation-flow-fn)
                        @(r/track creation-flow-fn))
               [button/square-secondary-open
                {:dom-id   (str "edit-stage-" pipeline-id "-" stage-id "-prevent-create")
                 :class (uu/cm (when prevent-create-disabled?
                                 "cursor-not-allowed"))
                 :disabled? prevent-create-disabled?
                 :on-click #(events/toggle-prevent-create!
                             {:pipeline-id pipeline-id
                              :stage-id stage-id})
                 :tooltip (cond first-stage?
                                (loc/tr :stage-config/first-stage-allow)

                                inherited?
                                (loc/tr :stage-config/not-allowed-inherited)

                                prevent-create?
                                (loc/tr :stage-config/click-to-allow)

                                last-stage?
                                (loc/tr :stage-config/click-to-prevent-last)

                                :else
                                (loc/tr :stage-config/click-to-prevent))}
                (if prevent-create?
                  [:i.fal.fa-lock]
                  [:i.fal.fa-lock-open])])
             [button/square-secondary-open
              {:dom-id    (str "edit-stage-" pipeline-id "-" stage-id "-delete-button")
               :disabled? (when prevent-delete?
                            "disabled")
               :tooltip   delete-tooltip
               :on-click  (fn []
                            (when-not prevent-delete?
                              (case (:entity/type stage)
                                :entity.type/stage
                                (modals/open-modal! stage-delete-modal/modal-key
                                                    {:stage-id stage-id})
                                :entity.type/opportunity-stage
                                (modals/open-modal! opportunity-stage-delete-modal/modal-key
                                                    {:opportunity-stage-id stage-id})
                                :entity.type/sales-opportunity-stage
                                (modals/open-modal!
                                 sales-opportunity-stage-delete-modal/modal-key
                                 {:sales-opportunity-stage-id stage-id})
                                :entity.type/exit-opportunity-stage
                                (modals/open-modal!
                                 exit-opportunity-stage-delete-modal/modal-key
                                 {:exit-opportunity-stage-id stage-id}))))}
              [:i.fal.fa-trash-alt]]]])]))}))

(defn stage-list
  [edit-state drag-state _temp-order-state _timeout-state
   {:keys [stages pipeline-id stage-name-attribute field-schema entity-atom]}]
  (let [stages (vec stages)
        pipeline @(r/track session/get-entity pipeline-id)
        creator-config (:pipeline/creator-config pipeline)
        stage-create-config (ecu/resolve-prevent-stage-config
                             {:config creator-config
                              :stages stages})
        prevent-delete? (= (count stages) 1)]
    [:div
     [:div
      {:class "pb-2 text-lg font-semibold text-base-light-text-paler"}
      (loc/tr :stages-currently-used)]

     [:div
      [sortable-list/view
       {:items                stages
        :get-key              :db/id
        :gap                  5
        :on-reorder           (fn [{:keys [source-index destination-index]}]
                                (let [item-id (get-in stages [source-index :db/id])
                                      prev-stages stages
                                      stages-with-new-ord (->> (cu/reorder-list prev-stages source-index destination-index)
                                                               (map-indexed (fn [index stage]
                                                                              (assoc stage :common/ord index)))
                                                               (seq))]
                                  (when stages-with-new-ord
                                    (fu/manage-entities session/reverse-lookup
                                                        session/session
                                                        stages-with-new-ord))

                                  (events/reorder!
                                   item-id
                                   source-index
                                   destination-index
                                   (fn [])
                                   (fn [_e]
                                     (logging/error "error reordering stage")
                                     (let [stages-by-id (->> prev-stages
                                                             (mapv (fn [stage]
                                                                     [(:db/id stage) stage]))
                                                             (into {}))]
                                       (swap! session/entities
                                              merge stages-by-id))))))
        :item-container-class "bg-transparent"
        :render-item          (fn [{:keys [item index]}]
                                (let [last-item? (= index (dec (count stages)))
                                      stage-id (:db/id item)]
                                  [stage-view
                                   prevent-delete?
                                   edit-state
                                   drag-state
                                   {:stage-name-attribute  stage-name-attribute
                                    :prevent-create-config (get-in stage-create-config
                                                                   [:prevent-create?
                                                                    :stages
                                                                    stage-id])
                                    :first-stage?          (zero? index)
                                    :last-stage?           last-item?
                                    :pipeline-id           pipeline-id
                                    :stage                 item
                                    :field-schema          field-schema
                                    :entity-atom           entity-atom}]))}]]]))

(defn processing
  [data-state]
  [:div
   {:class (uu/cm "p-8 relative hidden left-0 right-0 bottom-0 top-0" (when (:processing? @data-state) "flex justify-center items-center"))}
   [:div
    {:class "flex flex-col gap-3"}
    [:i.fal.fa-spinner.fa-pulse.fa-4x]
    [:span (loc/tr :processing)]]])

(defn view
  [{:keys [props state]}]
  (let [{:keys [pipeline-id stage-entity-type stage-name-attribute pipeline-attribute]} props

        data-state (r/cursor state [:data])
        edit-state (r/cursor state [:edit])
        drag-state (r/cursor state [:drag])
        temp-order-state (r/cursor state [:temp-order])
        timeout-state (r/cursor state [:timeout])
        tenant-schema @session/tenant-schema
        name-attribute-field-schema (registry/get-field-schema tenant-schema stage-name-attribute)]
    (fn [{:keys [shown? topmost? close-modal! props]}]
      (let [{:keys [header-text entity-atom]} props
            header-text (or header-text (loc/tr :pipeline-stages))
            header-text (case (events/view data-state)
                          :processing (loc/tr :processing)
                          header-text)
            stages (some->> (session/get-entities stage-entity-type)
                            (remove :common/disabled?)
                            (filter (fn [s]
                                      (= pipeline-id (get s pipeline-attribute))))
                            (sort-by :common/ord))

            processing? (= :processing (events/view data-state))]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         close-modal!
          :backdrop      "static"
          :close-button? true}
         header-text
         (if processing?
           [processing data-state]
           [stage-list edit-state drag-state temp-order-state timeout-state
            {:stages               stages
             :stage-entity-type    stage-entity-type
             :stage-name-attribute stage-name-attribute
             :pipeline-id          pipeline-id
             :pipeline-attribute   pipeline-attribute
             :field-schema         name-attribute-field-schema
             :entity-atom          entity-atom}])
         (when-not processing?
           [:<>
            ;[modal/left-cancel-only close-modal!]
            [:div]
            [modal/right-single-button
             nil
             false
             #(f-entity/open-creator! stage-entity-type
                                      {:title-text  (loc/tr :create-new-pipeline-stage)
                                       :start-value {pipeline-attribute pipeline-id}})
             (loc/tr :create-stage)
             "create-new-pipeline-stage-button"]])]))))
